import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, STATUS_LABELS, STATUS_CHIP, formatDateTR, formatDateTimeTR } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { Package, Factory, PackageCheck, Truck, Clock, AlertTriangle, ArrowRight, Boxes } from "lucide-react";
import { toast } from "sonner";

const KPI_COLORS = {
  URETIM_BEKLIYOR: "border-amber-500 text-amber-900 bg-amber-50",
  URETIMDE: "border-yellow-500 text-yellow-900 bg-yellow-50",
  HAZIR: "border-emerald-600 text-emerald-900 bg-emerald-50",
  SEVKIYATA_HAZIR: "border-sky-600 text-sky-900 bg-sky-50",
  SEVK_EDILDI: "border-blue-700 text-blue-900 bg-blue-50",
  SIPARIS_ALINDI: "border-slate-400 text-slate-800 bg-slate-100",
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  if (!stats) return <div className="text-slate-500">Yükleniyor...</div>;

  return (
    <div>
      <PageHeader title="Üretim Panosu" subtitle="Fabrika üretim durumu — güncel görünüm" />

      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <KpiCard testid="kpi-total-orders" icon={Boxes} label="Toplam Sipariş" value={stats.total_orders} accent="bg-slate-900 text-white border-slate-900" />
        <KpiCard testid="kpi-total-pieces" icon={Package} label="Toplam Fiziksel Parça" value={stats.total_pieces} accent="bg-blue-700 text-white border-blue-800" />
        <KpiCard testid="kpi-in-prod" icon={Factory} label="Üretimde" value={stats.status_counts.URETIMDE || 0} accent="bg-yellow-400 text-yellow-950 border-yellow-500" />
        <KpiCard testid="kpi-ready" icon={PackageCheck} label="Hazır" value={stats.status_counts.HAZIR || 0} accent="bg-emerald-500 text-white border-emerald-600" />
      </div>

      {/* Status Distribution */}
      <section className="mb-8">
        <SectionTitle>Durum Dağılımı</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {["SIPARIS_ALINDI","URETIM_BEKLIYOR","URETIMDE","HAZIR","SEVKIYATA_HAZIR","SEVK_EDILDI"].map((s) => (
            <div key={s} data-testid={`dist-${s}`} className={`card-industrial p-4 border-l-4 ${KPI_COLORS[s]}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{STATUS_LABELS[s]}</div>
              <div className="font-display font-black text-4xl tabular-nums mt-1">{stats.status_counts[s] || 0}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom: Upcoming + Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <SectionTitle icon={Truck}>Yaklaşan Sevkiyatlar</SectionTitle>
          <div className="card-industrial">
            {stats.upcoming_shipments.length === 0 && (
              <div className="p-8 text-slate-500 text-center">Yaklaşan sevkiyat bulunmuyor.</div>
            )}
            {stats.upcoming_shipments.map((o) => {
              const remaining = o.total - o.shipped;
              return (
                <Link
                  key={o.id}
                  to={`/siparisler/${o.id}`}
                  data-testid={`upcoming-${o.order_number}`}
                  className="flex items-center gap-4 px-4 py-3 border-b border-slate-200 last:border-0 hover:bg-slate-50 group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">{o.customer_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{o.order_number}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase text-slate-500 font-bold">Sevkiyat</div>
                    <div className="font-mono tabular-nums text-slate-900 font-semibold">{formatDateTR(o.requested_shipment_date)}</div>
                  </div>
                  <div className="text-right w-20">
                    <div className="text-xs uppercase text-slate-500 font-bold">Kalan</div>
                    <div className="font-display font-black text-xl tabular-nums text-red-600">{remaining}</div>
                  </div>
                  <ArrowRight size={18} className="text-slate-400 group-hover:text-blue-700" />
                </Link>
              );
            })}
          </div>
        </section>

        <section>
          <SectionTitle icon={Clock}>Son Aktivite</SectionTitle>
          <div className="card-industrial">
            {stats.recent_activity.length === 0 && (
              <div className="p-8 text-slate-500 text-center">Henüz aktivite yok.</div>
            )}
            {stats.recent_activity.map((r) => (
              <div key={r.id} className="px-4 py-3 border-b border-slate-200 last:border-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {r.prev_status && <span className={STATUS_CHIP[r.prev_status]}>{STATUS_LABELS[r.prev_status]}</span>}
                  <ArrowRight size={14} className="text-slate-400" />
                  {r.new_status && <span className={STATUS_CHIP[r.new_status]}>{STATUS_LABELS[r.new_status]}</span>}
                  <span className="ml-auto text-xs text-slate-500 font-mono">{formatDateTimeTR(r.timestamp)}</span>
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  <span className="font-semibold">{r.user_name}</span>
                  {r.extra?.barcode && <span className="ml-2 font-mono text-xs text-slate-500">Barkod: {r.extra.barcode}</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {stats.status_counts.URETIM_BEKLIYOR > 0 && (
        <div className="mt-8 p-4 border-l-4 border-amber-500 bg-amber-50 flex items-center gap-3">
          <AlertTriangle className="text-amber-700" />
          <div>
            <div className="font-bold text-amber-900">Üretim bekleyen parçalar var</div>
            <div className="text-sm text-amber-800">
              {stats.status_counts.URETIM_BEKLIYOR} parça üretim kuyruğunda. Barkod okutarak üretime alın.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`p-5 border-2 rounded-none flex items-center justify-between ${accent}`}>
      <div>
        <div className="text-xs uppercase tracking-widest font-bold opacity-90">{label}</div>
        <div className="font-display font-black text-5xl tabular-nums mt-1">{value}</div>
      </div>
      <Icon size={44} strokeWidth={2} className="opacity-80" />
    </div>
  );
}

function SectionTitle({ children, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon size={18} className="text-slate-600" />}
      <h2 className="font-display font-bold text-lg text-slate-900 uppercase tracking-wide">{children}</h2>
    </div>
  );
}
